import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const PRIMARY = '#00D2FF';
const SECONDARY = '#FF3CAC';
const ACCENT = '#FFE66D';
const BG = '#0B1120';

const fadeRange = (
  frame: number,
  inStart: number,
  inEnd: number,
  outStart: number,
  outEnd: number,
) => {
  const a = interpolate(frame, [inStart, inEnd], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const b = interpolate(frame, [outStart, outEnd], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return a * b;
};

const phaseOpacity = (frame: number, start: number, peakIn: number, peakOut: number, end: number) =>
  fadeRange(frame, start, peakIn, peakOut, end);

const GridBackdrop: React.FC<{ opacity: number; frame: number }> = ({ opacity, frame }) => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="nn-grid" width="48" height="48" patternUnits="userSpaceOnUse">
        <path
          d="M 48 0 L 0 0 0 48"
          fill="none"
          stroke="rgba(0,210,255,0.06)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="1920" height="1080" fill="url(#nn-grid)" transform={`translate(${frame % 48}, ${(frame * 0.3) % 48})`} />
  </svg>
);

const NeuralNode: React.FC<{
  cx: number;
  cy: number;
  r: number;
  fill: string;
  glow?: boolean;
  pulse?: number;
}> = ({ cx, cy, r, fill, glow, pulse = 1 }) => (
  <g>
    {glow && (
      <circle
        cx={cx}
        cy={cy}
        r={r * 2.2 * pulse}
        fill={fill}
        opacity={0.15}
      />
    )}
    <circle
      cx={cx}
      cy={cy}
      r={r * pulse}
      fill={fill}
      stroke="rgba(255,255,255,0.35)"
      strokeWidth="2"
    />
  </g>
);

const WeightedEdge: React.FC<{
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  strength: number;
  frame: number;
  pulsePhase: number;
}> = ({ x1, y1, x2, y2, strength, frame, pulsePhase }) => {
  const len = Math.hypot(x2 - x1, y2 - y1);
  const dash = interpolate(strength, [0, 1], [2, 5]);
  const offset = (frame * 4 + pulsePhase * 80) % (len * 2);
  const strokeW = interpolate(strength, [0, 1], [1, 3]);
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={`url(#edgeGrad-${pulsePhase % 3})`}
      strokeWidth={strokeW}
      strokeDasharray={`${dash} ${dash * 2}`}
      strokeDashoffset={-offset}
      opacity={0.35 + strength * 0.45}
    />
  );
};

const SingleNeuronDiagram: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const t = frame - 60;
  const inP = spring({ frame: t, fps, config: { damping: 14, stiffness: 120 } });
  const inputs = [0.3, 0.7, 0.5];
  const weights = [0.8, -0.4, 1.1];
  const bias = 0.2;
  const sum =
    inputs[0] * weights[0] + inputs[1] * weights[1] + inputs[2] * weights[2] + bias;
  const act = Math.max(0, sum);

  return (
    <div
      style={{
        position: 'absolute',
        left: 350,
        top: 140,
        opacity: opacity * inP,
        width: 1300,
      }}
    >
      <svg width="1300" height="600" viewBox="0 0 900 420">
        <defs>
          <linearGradient id="sn-in" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.9" />
            <stop offset="100%" stopColor={SECONDARY} stopOpacity="0.9" />
          </linearGradient>
        </defs>
        {inputs.map((v, i) => {
          const y = 80 + i * 110;
          const wx = interpolate(inP, [0, 1], [0, 1]) * 1;
          return (
            <g key={i}>
              <NeuralNode cx={100} cy={y} r={22} fill={PRIMARY} glow />
              <text x={100} y={y + 6} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700">
                x{i + 1}
              </text>
              <line
                x1={122}
                y1={y}
                x2={320}
                y2={200}
                stroke="url(#sn-in)"
                strokeWidth={3 + v * wx * 4}
                opacity={0.5 + v * 0.4}
              />
              <text x={200} y={(y + 200) / 2 - 12} fill={ACCENT} fontSize="16" fontWeight="600">
                w{i + 1} = {weights[i].toFixed(1)}
              </text>
            </g>
          );
        })}
        <NeuralNode cx={380} cy={200} r={38} fill={SECONDARY} glow pulse={1 + Math.sin(frame * 0.12) * 0.05} />
        <text x={380} y={208} textAnchor="middle" fill="#fff" fontSize="15" fontWeight="800">
          Σ+b
        </text>
        <line x1={418} y1={200} x2={520} y2={200} stroke={ACCENT} strokeWidth="4" opacity={inP} />
        <text x={450} y={185} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="14">
          激活函数
        </text>
        <rect
          x={520}
          y={130}
          width={200}
          height={140}
          rx={12}
          fill="rgba(255,255,255,0.04)"
          stroke={PRIMARY}
          strokeWidth="2"
          opacity={inP}
        />
        <path
          d="M 540 250 L 560 210 L 600 175 L 660 155 L 700 150"
          fill="none"
          stroke={ACCENT}
          strokeWidth="3"
          opacity={inP}
        />
        <text x={530} y={120} fill={PRIMARY} fontSize="15" fontWeight="700">
          ReLU: max(0, z)
        </text>
        <NeuralNode cx={740} cy={200} r={28} fill={ACCENT} glow />
        <text x={740} y={208} textAnchor="middle" fill="#0B1120" fontSize="14" fontWeight="800">
          a
        </text>
      </svg>
      <div
        style={{
          marginTop: 8,
          padding: '18px 28px',
          background: 'rgba(0,210,255,0.08)',
          borderRadius: 12,
          border: `1px solid ${PRIMARY}44`,
          color: 'rgba(255,255,255,0.85)',
          fontSize: 30,
          fontWeight: 600,
        }}
      >
        感知机：加权求和 + 偏置 → 非线性激活 → 输出信号
      </div>
      <div style={{ marginTop: 12, fontSize: 28, color: 'rgba(255,255,255,0.45)' }}>
        数值示例：z ≈ {sum.toFixed(2)} → ReLU → a ≈ {act.toFixed(2)}
      </div>
    </div>
  );
};

const DeepNetworkVisual: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const layers = [4, 6, 5, 3];
  const baseX = [260, 600, 940, 1280];
  const nodeY: number[][] = layers.map((n) => {
    const gap = 420 / (n + 1);
    return Array.from({ length: n }, (_, j) => 80 + gap * (j + 1));
  });

  const buildProgress = spring({
    frame: frame - 130,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const edges: { li: number; i: number; j: number; s: number }[] = [];
  for (let li = 0; li < layers.length - 1; li++) {
    for (let i = 0; i < layers[li]; i++) {
      for (let j = 0; j < layers[li + 1]; j++) {
        const h = (i * 17 + j * 31 + li * 7) % 1000;
        edges.push({ li, i, j, s: 0.3 + (h / 1000) * 0.7 });
      }
    }
  }

  const visibleLayers = Math.min(layers.length, 1 + Math.floor(buildProgress * 3.99));

  const layerNames = ['输入层', '隐藏层 1', '隐藏层 2', '输出层'];
  const layerDescs = ['原始特征数据', '低级特征提取', '高级特征组合', '最终预测结果'];
  const nodeLabels = [
    ['x₁', 'x₂', 'x₃', 'x₄'],
    ['h₁', 'h₂', 'h₃', 'h₄', 'h₅', 'h₆'],
    ['h₁\'', 'h₂\'', 'h₃\'', 'h₄\'', 'h₅\''],
    ['y₁', 'y₂', 'y₃'],
  ];

  const flowArrowP = spring({
    frame: frame - 170,
    fps,
    config: { damping: 16, stiffness: 100 },
  });

  return (
    <div style={{ position: 'absolute', inset: 0, opacity }}>
      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            fontSize: 44,
            fontWeight: 900,
            background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          深层神经网络结构
        </span>
      </div>

      {/* Network SVG */}
      <svg
        width="1920"
        height="700"
        viewBox="0 0 1540 600"
        style={{ position: 'absolute', left: 0, top: 100 }}
      >
        <defs>
          <linearGradient id="edgeGrad-0" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={PRIMARY} />
            <stop offset="100%" stopColor={SECONDARY} />
          </linearGradient>
          <linearGradient id="edgeGrad-1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={SECONDARY} />
            <stop offset="100%" stopColor={ACCENT} />
          </linearGradient>
          <linearGradient id="edgeGrad-2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={ACCENT} />
            <stop offset="100%" stopColor={PRIMARY} />
          </linearGradient>
          <marker id="flowArrow" markerWidth="10" markerHeight="8" refX="8" refY="4" orient="auto">
            <path d="M 0 0 L 10 4 L 0 8 Z" fill={ACCENT} opacity="0.7" />
          </marker>
        </defs>

        {/* Layer background brackets */}
        {layers.map((n, li) => {
          if (li >= visibleLayers) return null;
          const lx = baseX[li];
          const topY = nodeY[li][0] - 42;
          const bottomY = nodeY[li][n - 1] + 42;
          const bp = spring({
            frame: frame - 138 - li * 12,
            fps,
            config: { damping: 14, stiffness: 180 },
          });
          return (
            <g key={`bg-${li}`} opacity={bp * 0.35}>
              <rect
                x={lx - 50}
                y={topY}
                width={100}
                height={bottomY - topY}
                rx={14}
                fill={li === 0 ? PRIMARY : li === layers.length - 1 ? ACCENT : SECONDARY}
                opacity={0.06}
              />
              <rect
                x={lx - 50}
                y={topY}
                width={100}
                height={bottomY - topY}
                rx={14}
                fill="none"
                stroke={li === 0 ? PRIMARY : li === layers.length - 1 ? ACCENT : SECONDARY}
                strokeWidth="1.5"
                strokeDasharray="6 4"
                opacity={0.3}
              />
            </g>
          );
        })}

        {/* Flow arrows between layers */}
        {[0, 1, 2].map((li) => {
          if (li >= visibleLayers - 1) return null;
          const mx = (baseX[li] + baseX[li + 1]) / 2;
          return (
            <g key={`arrow-${li}`} opacity={flowArrowP * 0.8}>
              <line
                x1={mx - 30}
                y1={110}
                x2={mx + 20}
                y2={110}
                stroke={ACCENT}
                strokeWidth="2.5"
                markerEnd="url(#flowArrow)"
              />
              <text
                x={mx - 5}
                y={90}
                textAnchor="middle"
                fill="rgba(255,255,255,0.4)"
                fontSize="18"
                fontWeight="600"
              >
                W·x + b
              </text>
            </g>
          );
        })}

        {/* Edges */}
        {edges.map((e, idx) => {
          if (e.li >= visibleLayers - 1) return null;
          return (
            <WeightedEdge
              key={idx}
              x1={baseX[e.li]}
              y1={nodeY[e.li][e.i]}
              x2={baseX[e.li + 1]}
              y2={nodeY[e.li + 1][e.j]}
              strength={e.s}
              frame={frame}
              pulsePhase={idx}
            />
          );
        })}

        {/* Nodes + labels */}
        {layers.map((n, li) => {
          if (li >= visibleLayers) return null;
          const lx = baseX[li];
          return (
            <g key={li}>
              {Array.from({ length: n }, (_, j) => {
                const ly = nodeY[li][j];
                const delay = li * 8 + j * 3;
                const np = spring({
                  frame: frame - 140 - delay,
                  fps,
                  config: { damping: 14, stiffness: 200 },
                });
                const c = li === 0 ? PRIMARY : li === layers.length - 1 ? ACCENT : SECONDARY;
                return (
                  <g key={j} opacity={np}>
                    <NeuralNode cx={lx} cy={ly} r={26} fill={c} glow={li === layers.length - 1} />
                    <text
                      x={lx}
                      y={ly + 6}
                      textAnchor="middle"
                      fill={li === layers.length - 1 ? '#0B1120' : '#fff'}
                      fontSize="14"
                      fontWeight="800"
                    >
                      {nodeLabels[li][j]}
                    </text>
                  </g>
                );
              })}
              {/* Layer name */}
              <text
                x={lx}
                y={540}
                textAnchor="middle"
                fill={li === 0 ? PRIMARY : li === layers.length - 1 ? ACCENT : SECONDARY}
                fontSize="24"
                fontWeight="800"
              >
                {layerNames[li]}
              </text>
              {/* Layer description */}
              <text
                x={lx}
                y={568}
                textAnchor="middle"
                fill="rgba(255,255,255,0.4)"
                fontSize="18"
                fontWeight="500"
              >
                {layerDescs[li]}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Bottom explanation panel */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 80,
          right: 80,
          display: 'flex',
          gap: 24,
          opacity: flowArrowP,
        }}
      >
        {[
          { icon: '🔢', label: '输入', desc: '原始数据特征送入网络' },
          { icon: '⚙️', label: '变换', desc: '每层做加权求和 + 非线性激活' },
          { icon: '📊', label: '抽象', desc: '逐层提取更高级的特征表示' },
          { icon: '🎯', label: '输出', desc: '最终层产生分类或回归结果' },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '16px 20px',
              borderRadius: 14,
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${[PRIMARY, SECONDARY, SECONDARY, ACCENT][i]}33`,
              textAlign: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 32 }}>{item.icon}</span>
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: [PRIMARY, SECONDARY, SECONDARY, ACCENT][i],
                }}
              >
                {item.label}
              </span>
            </div>
            <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
              {item.desc}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ForwardPulseVisual: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const cycle = (frame - 240) % 90;
  const wave = interpolate(cycle, [0, 30, 60, 90], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const xPos = interpolate(wave, [0, 1], [200, 1500]);

  return (
    <div style={{ position: 'absolute', inset: 0, opacity, pointerEvents: 'none' }}>
      <svg width="1920" height="1080" style={{ position: 'absolute', left: 0, top: 140 }}>
        <defs>
          <radialGradient id="pulseGlow">
            <stop offset="0%" stopColor={ACCENT} stopOpacity="0.9" />
            <stop offset="100%" stopColor={PRIMARY} stopOpacity="0" />
          </radialGradient>
        </defs>
        {[0, 1, 2, 3].map((k) => {
          const lag = k * 18;
          const cx = interpolate(((frame - 240 - lag) % 100) / 100, [0, 1], [220, 1480]);
          return (
            <circle
              key={k}
              cx={cx}
              cy={340}
              r={28 + k * 6}
              fill="url(#pulseGlow)"
              opacity={0.45 - k * 0.1}
            />
          );
        })}
        <rect
          x={xPos - 4}
          y={300}
          width={8}
          height={80}
          rx={4}
          fill={ACCENT}
          opacity={0.85}
          filter="url(#blur1)"
        />
        <defs>
          <filter id="blur1">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>
      </svg>
      <div
        style={{
          position: 'absolute',
          right: 100,
          top: 220,
          width: 520,
          padding: 24,
          background: 'rgba(15,20,35,0.85)',
          borderRadius: 16,
          border: `1px solid ${PRIMARY}55`,
        }}
      >
        <h3 style={{ margin: 0, color: PRIMARY, fontSize: 30, fontWeight: 800 }}>前向传播</h3>
        <p style={{ margin: '14px 0 0', color: 'rgba(255,255,255,0.7)', fontSize: 26, lineHeight: 1.55 }}>
          数据从输入层逐层传递：每层对上一层的输出做线性变换与非线性激活，最终在输出层得到预测结果。
        </p>
      </div>
    </div>
  );
};

const LossCurvePanel: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const t = frame - 360;
  const progress = spring({ frame: Math.max(0, t), fps, config: { damping: 20, stiffness: 90 } });
  const pts: string[] = [];
  for (let i = 0; i <= 40; i++) {
    const u = i / 40;
    const loss = 2.5 * Math.exp(-u * 3.2) + 0.08 + Math.sin(u * 20) * 0.04;
    const x = 80 + u * 400 * progress;
    const y = 320 - loss * 100;
    pts.push(`${x},${y}`);
  }
  const d = `M ${pts.join(' L ')}`;

  return (
    <div style={{ position: 'absolute', left: 500, top: 120, opacity, width: 1200 }}>
      <h3 style={{ color: SECONDARY, fontSize: 40, fontWeight: 800, margin: '0 0 16px' }}>
        训练：损失下降
      </h3>
      <svg width="780" height="520" viewBox="0 0 520 360">
        <rect x="60" y="40" width="420" height="260" rx="8" fill="rgba(0,0,0,0.35)" stroke={PRIMARY} strokeOpacity="0.4" />
        <path d="M 60 300 L 480 300 M 60 40 L 60 300" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
        <text x="270" y="335" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="18">
          训练步数
        </text>
        <text
          x="25"
          y={190}
          textAnchor="middle"
          fill="rgba(255,255,255,0.45)"
          fontSize="18"
          transform="rotate(-90 25 190)"
        >
          损失 L
        </text>
        <path d={d} fill="none" stroke={SECONDARY} strokeWidth="3" strokeLinecap="round" />
        <path
          d={`${d} L ${80 + 400 * progress} 300 L 80 300 Z`}
          fill={SECONDARY}
          fillOpacity="0.12"
        />
      </svg>
      <div
        style={{
          marginTop: 16,
          maxWidth: 900,
          fontSize: 28,
          color: 'rgba(255,255,255,0.75)',
          lineHeight: 1.55,
        }}
      >
        通过反向传播与梯度下降，网络参数沿损失函数梯度方向更新，使预测逐步逼近真实标签——这是深度学习优化的核心思想。
      </div>
    </div>
  );
};

const SummaryPanel: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const p = spring({ frame: frame - 475, fps, config: { damping: 12, stiffness: 140 } });
  const items = [
    { t: '感知机', d: '最简单的可学习单元：加权输入 + 激活' },
    { t: '深度网络', d: '多层堆叠可学习层次化特征表示' },
    { t: '前向 + 反向', d: '前向算预测，反向传梯度更新权重' },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: `translate(-50%, -50%) scale(${0.92 + p * 0.08})`,
        opacity: opacity * p,
        width: 1100,
      }}
    >
      <h2
        style={{
          fontSize: 52,
          fontWeight: 900,
          margin: '0 0 20px',
          textAlign: 'center',
          background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        从感知机到深度学习
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map((it, i) => {
          const ip = spring({
            frame: frame - 485 - i * 10,
            fps,
            config: { damping: 14, stiffness: 180 },
          });
          return (
            <div
              key={i}
              style={{
                padding: '18px 26px',
                borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${i % 2 === 0 ? PRIMARY : SECONDARY}44`,
                opacity: ip,
                transform: `translateX(${(1 - ip) * 40}px)`,
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 800, color: ACCENT }}>{it.t}</div>
              <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.72)', marginTop: 6 }}>{it.d}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const NeuralNetworkAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgFade = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const titleP = spring({ frame: frame - 4, fps, config: { damping: 12, stiffness: 200 } });
  const titleY = interpolate(titleP, [0, 1], [-40, 0]);

  const opTitle = phaseOpacity(frame, 0, 8, 52, 72);
  const opNeuron = phaseOpacity(frame, 58, 75, 125, 145);
  const opDeep = phaseOpacity(frame, 128, 145, 225, 248);
  const opForward = phaseOpacity(frame, 232, 250, 345, 365);
  const opLoss = phaseOpacity(frame, 350, 368, 455, 478);
  const opSummary = phaseOpacity(frame, 465, 485, 520, 540);

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse at 50% 20%, #122040 0%, ${BG} 45%, #050810 100%)`,
        fontFamily: 'system-ui, -apple-system, "PingFang SC", sans-serif',
        overflow: 'hidden',
      }}
    >
      <GridBackdrop opacity={bgFade * 0.85} frame={frame} />

      <div
        style={{
          position: 'absolute',
          top: 44,
          left: 56,
          right: 56,
          opacity: opTitle * titleP,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 900,
            margin: 0,
            background: `linear-gradient(120deg, ${PRIMARY}, ${ACCENT}, ${SECONDARY})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          神经网络原理
        </h1>
        <p style={{ fontSize: 28, margin: '10px 0 0', color: 'rgba(255,255,255,0.45)' }}>
          从感知机到深度学习 · 信号、层与前向传播
        </p>
      </div>

      <SingleNeuronDiagram frame={frame} fps={fps} opacity={opNeuron} />
      <DeepNetworkVisual frame={frame} fps={fps} opacity={opDeep} />
      <ForwardPulseVisual frame={frame} fps={fps} opacity={opForward} />
      <LossCurvePanel frame={frame} fps={fps} opacity={opLoss} />
      <SummaryPanel frame={frame} fps={fps} opacity={opSummary} />

      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 56,
          fontSize: 24,
          color: 'rgba(255,255,255,0.28)',
        }}
      >
        硬核科普 · 通识教育系列
      </div>
    </AbsoluteFill>
  );
};
